import { IPatientWaitingListActionBody, IPatientWaitingListData } from "@/api_framework/api_modals/Patients";
import { IBookingSummaryResponseData } from "@/api_framework/api_modals/Bookings";
import { usePatientWaitingActionAPI } from "@/app/hooks/api_hooks/PWL/usePatientWaitingActionAPI";
import { usePatientWaitingGroomersListAPI } from "@/app/hooks/api_hooks/PWL/usePatientWaitingGroomerListAPI";
import { useVerifyUserProfilePinAPI } from "@/app/hooks/api_hooks/PWL/useVerifyUserProfilePinAPI";
import useRefetchPWL from "@/app/hooks/api_hooks/process/useReFetchPWL";
import useRefetchPWLStats from "@/app/hooks/api_hooks/process/useReFetchPWLStats";
import { useEffect, Fragment, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, Transition } from "@headlessui/react";

import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import previous from "@/assets/previous.svg";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { RxCross2 } from "react-icons/rx";
import { useNavigate } from "react-router";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import { useGroomerListAPI } from "@/app/hooks/api_hooks/PWL/useGroomerListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import smart from "@/assets/smart.svg";
import toast from "react-hot-toast";
import SendPinModal from "@/app/components/routes/modals/SendPinWhatsappModal";
import PetVaccinationStatus from "@/app/components/ui/Reusable/PetVaccinationStatus";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";
import BookingGiftsSection from "./BookingGiftsSection";
import { BsWhatsapp } from "react-icons/bs";

export interface IPWLActionModalState {
  actionModalData?: IPatientWaitingListData;
  buttonName: string;
  heading: string;
  description: string;
  status: string;
}

interface IBookingCommentsFormFields {
  provider: string;
  groomer_id?: string;
}
export interface IPWLActionModalProps {
  open: boolean;
  setOpen: (e: boolean) => void;
  actionModalData: any;
  source?: any;
  bookingSummaryData?: IBookingSummaryResponseData | null;
  isBookingSummaryInProgress?: boolean;
}

const formatRecommendedGroomerDisplayName = (displayName?: string) => {
  if (!displayName) {
    return "";
  }

  const normalizedDisplayName = displayName.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const toTitleCase = (value: string) =>
    value
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  const displayNameWithPhoneMatch = normalizedDisplayName.match(/^(.*?)[\s-]+(\d{6,15})$/);
  if (displayNameWithPhoneMatch) {
    const groomerName = toTitleCase(displayNameWithPhoneMatch[1].trim());
    const groomerPhone = displayNameWithPhoneMatch[2];
    return `${groomerName} (${groomerPhone})`;
  }

  return toTitleCase(normalizedDisplayName);
};

const PWLActionModal: React.FC<IPWLActionModalProps> = ({
  open,
  setOpen,
  actionModalData,
  source,
  bookingSummaryData = null,
  isBookingSummaryInProgress = false,
}) => {
  /* instantiations */
  const debug = false;
  const { veticDateFormat, HTMLLiTagsToArray } = useUtils();
  const { appointmentButtonMapping, appointmentStatusMapping } = useMappedConstants();

  const cancelButtonRef = useRef(null);

  const formHook = useForm<IBookingCommentsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });

  /* APIS */
  const refetchPWL = useRefetchPWL();
  const refetchPWLStats = useRefetchPWLStats();
  const { execute: processCheckIn } = usePatientWaitingActionAPI();
  const { execute: verifyUserProfilePin } = useVerifyUserProfilePinAPI();
  const fetchWaitingGroomerList = usePatientWaitingGroomersListAPI();
  const { execute: fetchClinicWiseGroomerList, groomerList } = useGroomerListAPI();

  const [nudgeNotPresent, setNudgeNotPresent] = useState(false);

  /* APIS Ends Here */
  const reConstructOLLI = (appointment_note: string): string => {
    const noteArray = appointment_note?.split("||")?.map((note) => note.trim());

    // Create an ordered list as an HTML string
    const olString = `<ol>${noteArray?.map((note) => `<li>${note}</li>`)?.join("") ?? ""}</ol>`;

    return olString;
  };

  const status = appointmentStatusMapping[actionModalData?.booking_status?.toLowerCase()];

  const buttonName = appointmentButtonMapping[actionModalData?.booking_status?.toLowerCase()];
  const heading = appointmentButtonMapping[actionModalData?.booking_status?.toLowerCase()];
  const isGroomingService =
    actionModalData?.service_details?.service_type_id === "7208bf0c-6e43-4e65-b357-315fb956ecd7" ||
    actionModalData?.service_details?.id === "d10441ad-4549-11ed-af80-d1c5db9bbce2";

  const constructCheckInBody = (): IPatientWaitingListActionBody => {
    return {
      source_module: source === "day_closing_module" ? "day_closing_module" : "pwl_module",
      booking_date: actionModalData?.booking_start_date,
      booking_end_date_time: actionModalData?.booking_end_date_time.replace("T", " "),
      booking_start_date_time: actionModalData?.booking_start_date_time?.replace("T", " "),
      booking_status: status,
      clinic_details: {
        id: actionModalData?.clinic_details?.id,
      },

      end_time: actionModalData?.booking_end_time,
      start_time: actionModalData?.booking_start_time,
      pet_details: {
        id: actionModalData?.pet_details?.id ? actionModalData?.pet_details?.id : actionModalData?.pet_id,
      },
      service_details: {
        service_id: actionModalData?.service_details?.id,
        service_name: actionModalData?.service_details?.name,
        sub_service_id: actionModalData?.subservice_details?.id,
        sub_service_name: actionModalData?.subservice_details?.name,
      },
      groomer_id: formHook.watch(`groomer_id`) ? [formHook.watch(`groomer_id`)] : undefined,
      staff_details: {
        id: actionModalData?.staff_details?.id
          ? actionModalData?.staff_details?.id
          : actionModalData?.staff_details?.groomer_id,
        staff_name: actionModalData?.staff_details?.name,
      },
      user_details: {
        id: actionModalData?.user_details?.id,
      },
      comments: actionModalData?.appointment_note?.startsWith("<ol>")
        ? actionModalData?.appointment_note
        : `${reConstructOLLI(actionModalData?.appointment_note)}`,
    };
  };

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const handleCheckInCheckout = async (isPinCleared = isPinGateCleared) => {
    if (!isPinCleared) {
      toast.error("Please verify the user PIN to continue");
      return;
    }
    const checkInBody = constructCheckInBody();
    await processCheckIn(actionModalData?.booking_id, checkInBody);

    processId &&
      navigate(
        `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
      );

    await refetchPWLStats();
    refetchPWL();
    setOpen(false);
  };

  useEffect(() => {
    if (debug) {
      fetchWaitingGroomerList();
    }
  }, []);


  useEffect(() => {
    if (isGroomingService && ["waiting", "upcoming", "no show"].includes(actionModalData?.booking_status?.toLowerCase()))
      fetchClinicWiseGroomerList(actionModalData?.booking_id);
  }, [actionModalData, isGroomingService]);
  useEffect(() => {
    if (actionModalData?.booking_status?.toLowerCase() === "attending") {
      setNudgeNotPresent(true);
    }
  }, [actionModalData?.booking_status]);
  useEffect(() => {
    if (!open) {
      setUserPinDigits(["", "", "", ""]);
      setIsPinVerified(false);
      setIsVerifyingPin(false);
      setIsPinSendModalOpen(false);
    }
  }, [open, actionModalData?.booking_id]);

  const alert_category_list = ["Vaccination", "Deworming", "Grooming Add Ons"];

  const recommendedGroomerId =
    groomerList?.recommended_groomer_details?.groomer_id ?? groomerList?.recommended_groomer_details?.id;
  const recommendedGroomerDisplayName = formatRecommendedGroomerDisplayName(
    groomerList?.recommended_groomer_details?.display_name,
  );
  const reducedGroomerListItem = useSelectMenuReducer(
    groomerList?.available_groomer_list ?? [],
    "display_name",
    "groomer_id",
  );
  const groomerListItem = reducedGroomerListItem;

  const isGroomerSelectionVisible = groomerList?.groomer_selection_visible;
  const bookingSummaryAddOnItems = [
    ...(bookingSummaryData?.addons?.vaccinations ?? []),
    ...(bookingSummaryData?.addons?.add_on_services ?? []),
    ...(bookingSummaryData?.addons?.add_on_boms ?? []),
    ...(bookingSummaryData?.addons?.diagnostics ?? []),
  ];

  const selectedGroomerId = formHook.watch("groomer_id");

  const shouldShowRecommendedGroomerBanner =
    recommendedGroomerDisplayName &&
    ["waiting", "upcoming", "no show"].includes(actionModalData?.booking_status?.toLowerCase()) &&
    isGroomingService;

  const [overrideRecommended, setOverrideRecommended] = useState(false);
  const [userPinDigits, setUserPinDigits] = useState(["", "", "", ""]);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const [isPinSendModalOpen, setIsPinSendModalOpen] = useState(false);
  const userPinInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const showGroomerSelectionOnlyIn =
    ["waiting"].includes(actionModalData?.booking_status?.toLowerCase()) &&
    isGroomingService;
  const shouldShowUserPinSection = actionModalData?.require_user_profile_pin === true;
  const isUserPinComplete = userPinDigits.every((digit) => /^\d$/.test(digit));
  const isPinGateCleared = !shouldShowUserPinSection || isPinVerified;
  const pinUserId = actionModalData?.user_id || actionModalData?.user_details?.id;

  const handleUserPinDigitChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(-1);
    setUserPinDigits((prev) => {
      const updatedDigits = [...prev];
      updatedDigits[index] = sanitizedValue;
      return updatedDigits;
    });
    if (sanitizedValue && index < 3) {
      userPinInputRefs.current[index + 1]?.focus();
    }
    if (isPinVerified) {
      setIsPinVerified(false);
    }
  };

  const handleUserPinKeyDown = (index: number, key: string) => {
    if (key !== "Backspace") {
      return;
    }

    if (userPinDigits[index]) {
      setUserPinDigits((prev) => {
        const updatedDigits = [...prev];
        updatedDigits[index] = "";
        return updatedDigits;
      });
      return;
    }

    if (index > 0) {
      setUserPinDigits((prev) => {
        const updatedDigits = [...prev];
        updatedDigits[index - 1] = "";
        return updatedDigits;
      });
      userPinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyUserPin = async () => {
    if (!isUserPinComplete || isVerifyingPin) {
      return;
    }
    if (!pinUserId || !actionModalData?.clinic_id || !actionModalData?.booking_id) {
      toast.error("Required booking details are missing for PIN verification");
      return;
    }
    const bookingStatus = actionModalData?.booking_status?.toLowerCase();
    const intent = bookingStatus === "attending" ? "checkout" : "checkin";

    setIsVerifyingPin(true);
    const response = await verifyUserProfilePin({
      pin: userPinDigits.join(""),
      user_id: pinUserId,
      clinic_id: actionModalData?.clinic_id,
      booking_id: actionModalData?.booking_id,
      intent,
      source: "VMS",
      created_by: "CM",
    });
    setIsVerifyingPin(false);

    if (response?.status) {
      setIsPinVerified(true);
      toast.success(response?.message ?? "PIN verified successfully");
      if (!isDisabled) {
        await handleCheckInCheckout(true);
      }
      return;
    }
    setIsPinVerified(false);
    setUserPinDigits(["", "", "", ""]);
    userPinInputRefs.current[0]?.focus();
    toast.error(response?.message ?? "PIN verification failed");
  };

  const handleSendPinViaChannel = () => {
    if (!pinUserId || !actionModalData?.user_details?.mobile) {
      toast.error("Customer phone number is missing");
      return;
    }

    setIsPinSendModalOpen(true);
  };

  const handlePinSendSuccess = () => {
    setIsPinVerified(false);
  };

  const isDifferentFromRecommended =
    shouldShowRecommendedGroomerBanner && selectedGroomerId && selectedGroomerId !== recommendedGroomerId;

  const isDisabled = !isGroomerSelectionVisible
    ? false
    : (showGroomerSelectionOnlyIn && !selectedGroomerId) || (isDifferentFromRecommended && !overrideRecommended);
  const finalActionDisabled = isDisabled || !isPinGateCleared;

  // const isDisabled = false;
  const commentLines = HTMLLiTagsToArray(bookingSummaryData?.booking?.comments ?? "")
    .map((line) => line?.trim())
    .filter((line) => line && line !== "[]");
  const discountCoupon =
    commentLines
      .find((line) => line.toLowerCase().includes("discount coupon:"))
      ?.split(":")
      .slice(1)
      .join(":")
      .trim() || "No Discount";
  const vaccinationDiary =
    commentLines
      .find((line) => line.toLowerCase().includes("client will bring vaccination diary:"))
      ?.split(":")
      .slice(1)
      .join(":")
      .trim() ||
    commentLines
      .find((line) => line.toLowerCase().includes("vaccination diary:"))
      ?.split(":")
      .slice(1)
      .join(":")
      .trim() ||
    "No";
  const additionalComments = commentLines
    .filter((line) => !/^other:?$/i.test(line))
    .filter((line) => !line.toLowerCase().includes("discount coupon:"))
    .filter((line) => !line.toLowerCase().includes("client will bring vaccination diary:"))
    .filter((line) => !line.toLowerCase().includes("vaccination diary:"))
    .map((line) => line.replace(/^other:\s*/i, ""));
  const hasCommentsData = Boolean(
    discountCoupon !== "No Discount" ||
    vaccinationDiary !== "No" ||
    additionalComments.length > 0,
  );
  const serviceDisplayName =
    bookingSummaryData?.service?.sub_service_name || bookingSummaryData?.service?.service_name || "Service";
  const serviceOfferCallout = bookingSummaryData?.service?.gift_id ? bookingSummaryData.service.offer_callout : null;
  return (
    <>
      {" "}
      <div>
        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 overflow-y-auto"
            initialFocus={cancelButtonRef}
            onClose={() => setOpen(false)}
          >
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
              </Transition.Child>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                &#8203;
              </span>

              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="inline-block w-full align-bottom bg-white rounded-[20px] text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-[699px]">
                  <div className="rounded-[24px]">
                    <div className="px-[24px] pt-[16px] flex justify-between items-start gap-3 bg-[#F5F5F5] rounded-t-[24px]">
                      <p className="text-[14px] font-semibold">
                        Are you sure, you want to <span className="font-semibold">{heading}</span> for this appointment?
                      </p>
                      <RxCross2
                        className="w-6 h-6 text-black font-bold cursor-pointer shrink-0"
                        onClick={() => setOpen(false)}
                      />
                    </div>
                    <div className="px-[24px] pt-[16px] pb-[14px] max-h-[80vh] overflow-visible bg-[#F5F5F5] rounded-b-[24px]">
                      <div className="overflow-hidden rounded-[10px] bg-[#EDEDED] p-[1px]">
                        <div className="hidden grid-cols-[1.2fr_1fr_1.3fr_1fr_1fr] gap-4 bg-[#EDEDED] px-4 py-3 text-[12px] font-medium text-[#24282F] sm:grid">
                          <p>Parent</p>
                          <p>Pet Details</p>
                          <p>Service</p>
                          <p>Time</p>
                          <p>Provider</p>
                        </div>
                        <div className="m-[2px] rounded-[10px] bg-white p-3">
                          {isBookingSummaryInProgress ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.2fr_1fr_1.3fr_1fr_1fr]">
                              {Array.from({ length: 5 }).map((_, index) => (
                                <div key={index} className="space-y-2">
                                  <div className="h-4 w-3/4 animate-pulse rounded bg-[#E9ECF2]" />
                                  <div className="h-3 w-1/2 animate-pulse rounded bg-[#E9ECF2]" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-4 text-[12px] text-[#232932] sm:grid-cols-[1.2fr_1fr_1.3fr_1fr_1fr]">
                              <div>
                                <p className="font-medium text-[#2B2E34]">{actionModalData?.user_details?.name}</p>
                              </div>
                              <div>
                                <p className="font-medium text-[#2B2E34]">{actionModalData?.pet_details?.name}</p>
                                <p className="mt-1 text-[#6F7685]">{actionModalData?.pet_details?.pet_breed}</p>
                              </div>
                              <div>
                                <p className="font-medium text-[#2B2E34]">
                                  {bookingSummaryData?.service?.sub_service_name || bookingSummaryData?.service?.service_name}
                                </p>
                                <p className="mt-1 text-[#6F7685]">{bookingSummaryData?.service?.type_name}</p>
                              </div>
                              <div>
                                <p className="font-medium text-[#2B2E34]">{veticDateFormat(actionModalData?.booking_start_date_time)}</p>
                              </div>
                              <div>
                                <p className="font-medium text-[#2B2E34]">{actionModalData?.staff_details?.name}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {hasCommentsData && (
                        <div className="mt-3 rounded-[10px] bg-[#FFFFFF] px-[12px] py-[8px]">
                          <p className="text-[14px] font-semibold leading-none text-[#000000]">Comments</p>
                          <div className="mt-4 space-y-2">
                            <div className="grid grid-cols-[140px_1fr] items-start gap-3">
                              <p className="text-[12px] font-medium text-[#6D6D6D]">Discount Coupon</p>
                              <p className="text-[12px] font-semibold text-[#222222]">{discountCoupon}</p>
                            </div>
                            <div className="grid grid-cols-[140px_1fr] items-start gap-3">
                              <p className="text-[12px] font-medium text-[#6D6D6D]">Vaccination Dairy</p>
                              <p className="text-[12px] font-semibold text-[#222222]">{vaccinationDiary}</p>
                            </div>
                          </div>
                          <div className="mt-4 rounded-[8px] bg-[#F0F0F0] p-[8px]">
                            <p className="text-[12px] font-medium text-[#000000]">Additional Comments</p>
                            {additionalComments.length > 0 ? (
                              <ul className="mt-2 list-disc pl-5 text-[12px] text-[#1F1F23]">
                                {additionalComments.map((comment, index) => (
                                  <li key={`${comment}-${index}`}>{comment}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-2 text-[14px] font-medium text-[#2B2E34]">No additional comments</p>
                            )}
                          </div>
                        </div>
                      )}
                      {["upcoming", "waiting", "no show"].includes(actionModalData?.booking_status?.toLowerCase()) &&
                        isGroomingService && isGroomerSelectionVisible && (
                         <div className={ shouldShowRecommendedGroomerBanner || showGroomerSelectionOnlyIn ? "col-span-3 relative z-[90] mt-2 rounded-[10px] border border-[#F1E8D6] bg-[#FEFBF5] p-[12px]": ""}>
                            {showGroomerSelectionOnlyIn && (
                              <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[170px_1fr]">
                                <p className="text-[14px] font-semibold text-black">
                                  Select Groomer <span className="text-red-500">*</span>
                                </p>
                                <div className="w-full pt-1 font-medium sm:text-xs 2xl:text-sm">
                                  <SearchableSelectMenu
                                    errorMessages={[{ message: "", type: "required" }]}
                                    onSelectItem={(item) => {
                                      if (item) {
                                        formHook.setValue(`groomer_id`, item.id);
                                        setOverrideRecommended(false);
                                      }
                                    }}
                                    fieldError={formHook?.formState?.errors?.groomer_id}
                                    register={formHook.register(`groomer_id`, { required: true })}
                                    selectItems={groomerListItem}
                                    placeholder="Select Groomer"
                                    showTooltips={true}
                                    showTypedErrors={false}
                                    showDropdownIcon={true}
                                    defaultSelected={
                                      groomerListItem?.filter((oc) => oc.id === formHook.watch(`groomer_id`))[0]
                                    }
                                    listBoxClassName="z-[120] mt-1 max-h-60 w-full rounded-[10px] border border-[#E2E2E2] bg-white shadow-[0_16px_30px_rgba(15,23,42,0.16)]"
                                    className="w-full rounded-[10px] border-[1px] border-[#D9DEE7] bg-white text-[12px] text-[#2B2E34]"
                                    containerClassName="w-full z-[120]"
                                  />
                                </div>
                              </div>
                            )}

                            {shouldShowRecommendedGroomerBanner && (
                              <div className="flex items-center mt-2  justify-around ">
                                <div className=" flex items-center justify-end gap-2">
                                  <p className="text-[12px] font-semibold uppercase tracking-wide text-[#C34C01]">Recommended Groomer</p>
                                  <p className="text-[12px] font-semibold text-[#000000]">{recommendedGroomerDisplayName}</p>
                                </div>
                              </div>
                            )}

                          </div>
                        )}
                      {["upcoming", "waiting", "no show"].includes(actionModalData?.booking_status?.toLowerCase()) && (
                        <div className={`${nudgeNotPresent ? "pt-0 pb-0" : "sm:pt-[20px] 2xl:pt-[30px] pb-[24px]"}`}>
                          <NudgesContainer
                            source="PWL"
                            alert_category_list={alert_category_list}
                            customer_id={actionModalData?.user_details?.id}
                            pet_Id={actionModalData?.pet_details?.id}
                            setNudgeNotPresent={setNudgeNotPresent}
                            service_id={actionModalData?.service_details?.id}
                            sub_service_id={actionModalData?.subservice_details?.id}
                          />
                        </div>
                      )}
                    </div>

                    <section className="mb-4 bg-white p-3">
                      <h3 className="text-[14px] font-semibold text-[#000000]">Items in Cart</h3>
                      {isBookingSummaryInProgress ? (
                        <div className="mt-3 space-y-3">
                          {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} className="h-16 animate-pulse  bg-[#F1F3F8]" />
                          ))}
                        </div>
                      ) : (
                        bookingSummaryData?.service ||
                        bookingSummaryData?.addons?.memberships?.[0] ||
                        bookingSummaryAddOnItems.length > 0
                      ) ? (
                        <>
                          {bookingSummaryData?.addons?.memberships?.[0] && (
                            <div
                              className="mt-3 rounded-[12px] border border-[#D6FFE8] p-3"
                              style={{
                                background:
                                  "radial-gradient(ellipse at 50% 0%, rgba(164, 255, 181, 0.35) 0%, rgba(164, 255, 181, 0) 72%), linear-gradient(111deg, #FFFFFF 29%, rgba(164, 255, 181, 0.22) 106%)",
                              }}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-[14px] font-medium text-[#222833]">
                                    {bookingSummaryData?.addons?.memberships?.[0]?.name}
                                  </p>
                                  <div className="mt-1 flex flex-wrap items-center gap-2">
                                    {bookingSummaryData?.addons?.memberships?.[0]?.duration && (
                                      <p className="text-[12px] font-medium text-[#6D6D6D]">
                                        {/min/i.test(bookingSummaryData?.addons?.memberships?.[0]?.duration || "")
                                          ? bookingSummaryData?.addons?.memberships?.[0]?.duration
                                          : `${bookingSummaryData?.addons?.memberships?.[0]?.duration} mins`}
                                      </p>
                                    )}

                                    <span
                                      className="px-2 py-0.5 text-xs font-medium flex gap-2"
                                      style={{
                                        background: "linear-gradient(111.32deg, #EAFFE6 29.47%, #C7FFD2 106.2%)",
                                        borderRadius: "44px",
                                      }}
                                    >
                                      <img src={smart} alt="Smart Choice" className="h-3.5 w-3.5" />
                                      <p className="text-[#079A27]">  Smart Choice</p>
                                    </span>
                                  </div>
                                  {(Array.isArray(bookingSummaryData?.addons?.memberships?.[0]?.details)
                                    ? bookingSummaryData?.addons?.memberships?.[0]?.details?.join(", ")
                                    : bookingSummaryData?.addons?.memberships?.[0]?.details) && (
                                      <ul className="mt-3 space-y-1 text-[12px] font-medium text-[#016516]">
                                        <li>{`\u2022 ${Array.isArray(bookingSummaryData?.addons?.memberships?.[0]?.details)
                                          ? bookingSummaryData?.addons?.memberships?.[0]?.details?.join(", ")
                                          : bookingSummaryData?.addons?.memberships?.[0]?.details
                                          }`}</li>
                                      </ul>
                                    )}
                                </div>
                                <div className="text-right">
                                  <p className="text-[14px] font-semibold text-[#1F2431]">
                                    {bookingSummaryData?.addons?.memberships?.[0]?.final_price == null
                                      ? "—"
                                      : Number(bookingSummaryData?.addons?.memberships?.[0]?.final_price) === 0
                                      ? "Free"
                                      : `₹${Number(bookingSummaryData?.addons?.memberships?.[0]?.final_price ?? 0).toLocaleString(
                                        "en-IN",
                                        { maximumFractionDigits: 2 },
                                      )}`}
                                  </p>
                                  {Number(bookingSummaryData?.addons?.memberships?.[0]?.price ?? 0) >
                                    Number(bookingSummaryData?.addons?.memberships?.[0]?.final_price ?? 0) && (
                                      <p className="text-[12px] text-[#8B92A1] line-through">
                                        {`₹${Number(bookingSummaryData?.addons?.memberships?.[0]?.price ?? 0).toLocaleString(
                                          "en-IN",
                                          { maximumFractionDigits: 2 },
                                        )}`}
                                      </p>
                                    )}
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="mt-3 rounded-[10px] bg-white">
                            {bookingSummaryData?.service &&
                              (bookingSummaryData.service.type_name?.toLowerCase() === "vaccination" ? (
                                <div className="px-[4px] py-[4px] bg-[#FFFFFF]">
                                  <p className="text-[14px] font-medium text-[#1F2431]">Vaccination</p>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-2 border-b border-dashed border-[#EAEAEB] py-3 last:border-b-0 sm:flex-row sm:items-end sm:justify-between">
                                  <div className="flex min-w-0 items-center gap-2.5">
                                    {bookingSummaryData?.service?.image_url ? (
                                      <img
                                        src={bookingSummaryData?.service?.image_url}
                                        alt={`${serviceDisplayName} image`}
                                        className="h-[50px] w-[50px] rounded-[10px] object-cover"
                                      />
                                    ) : (
                                      <div className="h-[50px] w-[50px] rounded-[10px] bg-[#F2F4F8]" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="flex min-w-0 items-center gap-1.5">
                                        <p className="truncate text-[14px] font-medium text-[#222222]">
                                          {serviceDisplayName}
                                        </p>
                                        {serviceOfferCallout && <OfferCalloutBadge callout={serviceOfferCallout} />}
                                      </div>
                                      <p className="line-clamp-2 text-[12px] text-[#6D6D6D]">{bookingSummaryData?.service?.type_name}</p>
                                    </div>
                                  </div>
                                  <div className="ml-auto flex flex-col items-end justify-end gap-2 text-right">
                                    {bookingSummaryData?.service?.duration && (
                                      <span className="rounded-[8px] bg-[#F2F2F2] px-2 py-0.5 text-[11px] font-medium text-[#666666]">
                                        {`${bookingSummaryData?.service?.duration} mins`}
                                      </span>
                                    )}
                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                      {Number(bookingSummaryData?.service?.discount_percent ?? 0) > 0 && (
                                        <>
                                          <p className="text-[12px] font-medium text-[#239A3D]">
                                            {`${bookingSummaryData?.service?.discount_percent ?? 0}%`}
                                          </p>
                                          {Number(bookingSummaryData?.service?.price ?? 0) >
                                            Number(bookingSummaryData?.service?.final_price ?? 0) && (
                                              <p className="text-[12px] text-[#222222] line-through">
                                                {`₹${(bookingSummaryData?.service?.price ?? 0).toLocaleString("en-IN")}`}
                                              </p>
                                            )}
                                        </>
                                      )}
                                      <p className="text-[14px] font-semibold text-[#1F2431]">
                                        {bookingSummaryData?.service?.final_price == null
                                          ? "—"
                                          : bookingSummaryData?.service?.final_price === 0
                                          ? "Free"
                                          : `₹${(bookingSummaryData?.service?.final_price ?? 0).toLocaleString("en-IN")}`}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {(() => {
                              const firstVaccineIndex = bookingSummaryAddOnItems.findIndex(
                                (x) => x.type === "vaccine" || Boolean((x as any)?.vaccine_name)
                              );
                              return bookingSummaryAddOnItems.map((item, index) => {
                                const originalPrice = Number(item.price ?? 0);
                                const finalPrice = Number(item.final_price ?? item.price ?? 0);
                                const isPriceMissing = item.final_price == null && item.price == null;
                                const discountPercent = Number(item.discount_percent ?? 0);
                                // const durationLabel = item?.duration ? `${item?.duration} mins` : null;
                                const imageUrl = item.image_url?.trim() || null;
                                const vaccineName = (item as any)?.vaccine_name;
                                const isVaccine = item.type === "vaccine" || Boolean(vaccineName);
                                const showImageForVaccine = isVaccine && index === firstVaccineIndex;

                                return (
                                  <div
                                    key={item.id}
                                    className="flex bg-[#FFFFFF]"
                                  >
                                    {/* Left: Image or spacer */}
                                    <div className="flex shrink-0 items-center pl-[12px] py-[14px]">
                                      {(!isVaccine || showImageForVaccine) ? (
                                        imageUrl ? (
                                          <img
                                            src={imageUrl}
                                            alt={`${vaccineName || item.name} image`}
                                            className="h-[50px] w-[50px] rounded-[10px] object-cover"
                                          />
                                        ) : (
                                          <div
                                            className="h-[50px] w-[50px] rounded-[10px] bg-[#F2F4F8]"
                                            aria-label={`${vaccineName || item.name} image not available`}
                                          />
                                        )
                                      ) : (
                                        <div className="w-[50px] shrink-0" />
                                      )}
                                    </div>

                                    {/* Right: Content block */}
                                    <div className={`flex flex-1 min-w-0 justify-between items-center gap-2 pr-[12px] pl-[10px] py-[14px] ${index === bookingSummaryAddOnItems.length - 1 ? "" : "border-b border-dashed border-[#EAEAEB]"}`}>
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="truncate text-[14px] font-medium text-[#222222]">{vaccineName || item.name}</p>
                                          {item.gift_id && item.offer_callout && <OfferCalloutBadge callout={item.offer_callout} />}
                                          {item.previously_added && (
                                            <span
                                              className="shrink-0 inline-flex items-center gap-1 rounded-[44px] px-2 py-0.5 text-[10px] font-medium text-[#4375FF]"
                                              style={{
                                                background: "linear-gradient(111.32deg, #F2F4FF 29.47%, #D0DDFF 106.2%)",
                                                border: "0.5px solid #8CA9FA",
                                              }}
                                            >
                                              <img src={previous} alt="previous" className="h-3 w-3" />
                                              Previously added
                                            </span>
                                          )}
                                        </div>
                                        {item.type === "vaccine" || Boolean(vaccineName) ? (
                                          (item as any)?.vaccination_details ? (
                                            <div className="flex items-center gap-1.5 text-[12px] text-[#6D6D6D]">
                                              {(item as any).vaccination_details.status && (
                                                <PetVaccinationStatus
                                                  status={(item as any).vaccination_details.status}
                                                  type="vaccination"
                                                  className="pointer-events-none no-underline"
                                                />
                                              )}
                                              {(item as any).vaccination_details.due_date ? (
                                                <span>{veticDateFormat((item as any).vaccination_details.due_date)}</span>
                                              ) : null}
                                            </div>
                                          ) : null
                                        ) : (
                                          <p className="line-clamp-3 text-[12px] text-[#6D6D6D]">{item.description}</p>
                                        )}
                                      </div>

                                      {/* Right: Price */}
                                      <div className="ml-auto flex flex-col items-end justify-end gap-2 text-right">
                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                          {discountPercent > 0 && (
                                            <>
                                              <p className="text-[12px] font-medium text-[#239A3D]">{`${discountPercent}%`}</p>
                                              {originalPrice > finalPrice && (
                                                <p className="text-[12px] text-[#222222] line-through">
                                                  {`₹${originalPrice.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                                                </p>
                                              )}
                                            </>
                                          )}
                                          <p className="text-[14px] font-semibold text-[#1F2431]">
                                            {isPriceMissing
                                              ? "—"
                                              : finalPrice === 0
                                              ? "Free"
                                              : `₹${finalPrice.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </>
                      ) : (
                        <p className="mt-3 text-[12px] font-medium text-[#6D6D6D]">No items available.</p>
                      )}
                    </section>
                    {!isBookingSummaryInProgress && (
                      <BookingGiftsSection
                        giftClaimReminders={bookingSummaryData?.gift_details ?? actionModalData?.gift_details ?? []}
                      />
                    )}
                    {shouldShowUserPinSection && (
                      <section className="mb-4 rounded-[10px] bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <p className="text-[14px] font-semibold text-[#000000]">User Pin</p>
                            <div className="flex items-center gap-2">
                              {userPinDigits.map((digit, index) => (
                                <input
                                  key={`user-pin-${index}`}
                                  ref={(element) => {
                                    userPinInputRefs.current[index] = element;
                                  }}
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="one-time-code"
                                  maxLength={1}
                                  value={digit}
                                  onChange={(event) => handleUserPinDigitChange(index, event.target.value)}
                                  onKeyDown={(event) => handleUserPinKeyDown(index, event.key)}
                                  className="h-10 w-10 rounded-[8px] border border-[#D9DEE7] text-center text-[16px] font-semibold text-[#111111] outline-none focus:border-[#111111]"
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleVerifyUserPin}
                              disabled={!isUserPinComplete || isVerifyingPin}
                              className={`h-[40px] rounded-[8px] px-4 text-[12px] font-semibold transition-all ${isUserPinComplete && !isVerifyingPin
                                ? "bg-[#111111] text-white hover:opacity-95"
                                : "cursor-not-allowed bg-[#B9B9B9] text-[#F3F3F3]"
                                }`}
                            >
                              {isVerifyingPin ? "Verifying..." : "Verify PIN"}
                            </button>
                            <button
                              type="button"
                              onClick={handleSendPinViaChannel}
                              className="h-[40px] rounded-[8px] border border-[#25D366] bg-white px-4 text-[12px] font-semibold text-[#25D366] transition-colors hover:bg-[#E8F5E9] flex items-center gap-2 justify-center"
                            >
                              Send via
                              <BsWhatsapp className="w-4 h-4" />

                            </button>
                          </div>
                        </div>
                      </section>
                    )}
                    <div className="sticky bottom-0 z-20 mt-4 rounded-b-[24px] border-t border-[#DCDCDC] bg-[#FFFFFF] py-[12px] px-[24px] shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
                      {isDifferentFromRecommended && (
                        <SimpleCheckbox
                          onChange={() => setOverrideRecommended(!overrideRecommended)}
                          checked={overrideRecommended}
                          id="overrideRecommended"
                          colorClassName="!h-10 !w-10 rounded-[12px] border-[#9B9B9B] text-[#202223] cursor-pointer focus:ring-0"
                          containerClassName="items-center py-1"
                          descriptionContent={
                            <label
                              htmlFor="overrideRecommended"
                              className="ml-3 block cursor-pointer text-[15px] font-semibold leading-[1.25] text-[#111111]"
                            >
                              I have selected a different groomer than recommended by central team.
                            </label>
                          }
                        />
                      )}
                      <div className="mt-4 flex justify-between">
                        <button
                          onClick={() => setOpen(false)}
                          className="flex w-[252px] h-[49px] items-center justify-center rounded-[10px] border border-transparent bg-transparent text-[14px] font-semibold text-[#000000] transition-colors hover:bg-[#ECECEC]"
                        >
                          Go Back
                        </button>
                        <button
                          disabled={finalActionDisabled}
                          onClick={() => handleCheckInCheckout()}
                          className={`flex h-[49px] w-[388px] items-center py-[14px] px-[8px] justify-center rounded-[10px] text-[14px] font-semibold transition-all ${finalActionDisabled
                            ? "cursor-not-allowed bg-[#939393] text-[#F3F3F3]"
                            : "bg-[#111111] text-white hover:opacity-95"
                            }`}
                        >
                          {buttonName}
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
        <SendPinModal
          open={isPinSendModalOpen}
          setOpen={setIsPinSendModalOpen}
          userId={pinUserId}
          userMobileNumber={actionModalData?.user_details?.mobile}
          bookingId={actionModalData?.booking_id}
          clinicId={actionModalData?.clinic_id}
          intent={actionModalData?.booking_status?.toLowerCase() === "attending" ? "checkout" : "checkin"}
          onSuccess={handlePinSendSuccess}
        />
      </div>
    </>
  );
};

export default PWLActionModal;
